import React from 'react';
import { Wine, ShoppingBag, Star, ChevronLeft } from 'lucide-react';
import { WalletTierDesign, walletSample, nextTierName } from '../../data/mockDigitalWallet';
export type WalletPlatform = 'apple' | 'google';
interface WalletPassPreviewProps {
  tier: WalletTierDesign;
  platform: WalletPlatform;
  side?: 'front' | 'back';
  logoText?: string;
  /** Shows the latest push on the back of the pass */
  latestNotification?: {
    title: string;
    message: string;
  } | null;
  compact?: boolean;
}

// A simple, decorative QR stand-in
function QrMock() {
  const cells = Array.from({
    length: 21 * 21
  }).map((_, i) => {
    const x = i % 21;
    const y = Math.floor(i / 21);
    const finder = x < 7 && y < 7 || x > 13 && y < 7 || x < 7 && y > 13;
    if (finder) {
      const fx = x > 13 ? x - 14 : x;
      const fy = y > 13 ? y - 14 : y;
      return fx === 0 || fx === 6 || fy === 0 || fy === 6 || fx > 1 && fx < 5 && fy > 1 && fy < 5;
    }
    return (x * 7 + y * 13 + x * y) % 5 < 2;
  });
  return <div className="grid bg-white p-2 rounded" style={{
    gridTemplateColumns: 'repeat(21, 1fr)',
    width: 120,
    height: 120
  }} data-id="element-2808" data-mp="14gyej1smvv63">
      {cells.map((on, i) => <div key={i} style={{
      background: on ? '#111' : 'transparent'
    }} data-id="element-2809" data-mp="uq3qpszqonij" data-mpi={i} />)}
    </div>;
}
function Field({
  label,
  value,
  tier,
  align = 'left'
}: {
  label: string;
  value: string;
  tier: WalletTierDesign;
  align?: 'left' | 'right';
}) {
  return <div className={align === 'right' ? 'text-right' : ''} data-id="element-2810" data-mp="1ne9bgfqsmcid">
      <div className="text-[9px] font-semibold uppercase tracking-wide" style={{
      color: tier.label
    }} data-id="element-2811" data-mp="1ih0ugihdseip">
        {label}
      </div>
      <div className="text-[13px] font-medium" style={{
      color: tier.text
    }} data-id="element-2812" data-mp="1eggy3h4mtexm">
        {value}
      </div>
    </div>;
}
export function WalletPassPreview({
  tier,
  platform,
  side = 'front',
  logoText = 'Acme Estate',
  latestNotification = null,
  compact = false
}: WalletPassPreviewProps) {
  const width = compact ? 260 : 300;
  if (side === 'back') {
    const rows: [string, React.ReactNode][] = [['Your benefits', walletSample.benefits], ['Rewards breakdown', walletSample.rewardsBreakdown], ['Member since', walletSample.memberSince], ['Club', walletSample.club], ['Notifications', latestNotification ? <span data-id="element-2813" data-mp="3qnkrmqtulo0z">
            <span className="font-medium text-[#0F172A]" data-id="element-2814" data-mp="3qn1le7dk8o2s">{latestNotification.title}</span>
            <br data-id="element-2815" data-mp="3cwfzsawq2017" />
            {latestNotification.message}
          </span> : 'No new notifications'], ['View my rewards', <span className="text-[#2563EB]" data-id="element-2816" data-mp="3hneljq0hbqbu">View my rewards in the member portal</span>], ['Shop', <span className="text-[#2563EB]" data-id="element-2817" data-mp="32ya1s4e1de1m">Shop our wines</span>]];
    return <div className="bg-[#F2F2F7] rounded-[28px] p-4 shadow-inner" style={{
      width
    }} data-id="element-2818" data-mp="32vxah0me1rbw">
        <div className="flex items-center gap-2 mb-3" data-id="element-2819" data-mp="37v0fwm6a5zke">
          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm" data-id="element-2820" data-mp="2tyd63it8bex2">
            <ChevronLeft className="w-4 h-4 text-[#0F172A]" data-id="element-2821" data-mp="2yvdbdri98dn4" />
          </div>
        </div>
        <div className="text-center mb-3" data-id="element-2822" data-mp="2f9tnm879njp8">
          <div className="text-[15px] font-semibold text-[#0F172A]" data-id="element-2823" data-mp="2fav7n269fgj3">{logoText}</div>
          <div className="text-[11px] text-[#64748B]" data-id="element-2824" data-mp="2ad38po776u5e">Updated today</div>
        </div>
        <div className="bg-white rounded-xl divide-y divide-[#E5E7EB]" data-id="element-2825" data-mp="2oc2w6siz52zz">
          {rows.map(([label, value]) => <div key={label} className="px-3 py-2" data-id="element-2826" data-mp="2k8wttvyzfvys">
              <div className="text-[11px] text-[#0F172A] font-medium" data-id="element-2827" data-mp="2y9h8qh1s17n0">{label}</div>
              <div className="text-[11px] text-[#64748B] mt-0.5" data-id="element-2828" data-mp="2y74k5l1ijz0z">{value}</div>
            </div>)}
        </div>
      </div>;
  }
  if (platform === 'google') {
    return <div className="rounded-[22px] overflow-hidden shadow-lg" style={{
      width,
      background: tier.background
    }} data-id="element-2829" data-mp="37xz9iya23eqn">
        <div className="px-4 pt-4 pb-3 flex items-center gap-2" data-id="element-2830" data-mp="332aftbkpws29">
          <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center" data-id="element-2831" data-mp="3h0qlr0araejc">
            <Wine className="w-4 h-4" style={{
            color: tier.background
          }} data-id="element-2832" data-mp="3c1xaleixyzmc" />
          </div>
          <span className="text-[13px] font-medium" style={{
          color: tier.text
        }} data-id="element-2833" data-mp="1ab6ttnw6hp0d">
            {logoText}
          </span>
        </div>
        <div className="px-4 pb-3" data-id="element-2834" data-mp="1k1rq34odqhg4">
          <div className="text-[11px]" style={{
          color: tier.label
        }} data-id="element-2835" data-mp="1k2t1595eb3xu">
            {tier.name} member
          </div>
          <div className="text-[20px] font-semibold" style={{
          color: tier.text
        }} data-id="element-2836" data-mp="1o57mbzwjefsa">
            {walletSample.fullName}
          </div>
        </div>
        <div className="px-4 pb-3 grid grid-cols-3 gap-2" data-id="element-2837" data-mp="wcqc71ws2e3g">
          <Field label="Points" value={walletSample.points} tier={tier} data-id="element-2838" data-mp="10dsbtjulws5y" />
          <Field label="Next tier" value={nextTierName(tier.id)} tier={tier} data-id="element-2839" data-mp="10fvdx291abr1" />
          <Field label="Rewards" value={walletSample.rewards} tier={tier} align="right" data-id="element-2840" data-mp="15dwungizlyzc" />
        </div>
        <div className="flex justify-center pb-4" data-id="element-2841" data-mp="2bsfqufm55kt9">
          <QrMock data-id="element-2842" data-mp="2gr901vueapnm" />
        </div>
        <div className="h-[86px]" style={{
        background: tier.banner
      }} data-id="element-2843" data-mp="2gq7e2wbucir4" />
      </div>;
  }
  return <div style={{
    width
  }} data-id="element-2844" data-mp="2quxlhhq0kf2q">
      <div className="rounded-[14px] overflow-hidden shadow-lg" style={{
      background: tier.background
    }} data-id="element-2845" data-mp="2lwm9b0c8xshc">
        <div className="px-4 pt-3 pb-2 flex items-start justify-between" data-id="element-2846" data-mp="2h5e0o6iqqb58">
          <div className="flex items-center gap-1.5 pt-1" data-id="element-2847" data-mp="2h3jiv12rmz5d">
            <Wine className="w-4 h-4" style={{
            color: tier.text
          }} data-id="element-2848" data-mp="2d3stn1sw0r2y" />
            <span className="text-[14px] font-semibold" style={{
            color: tier.text
          }} data-id="element-2849" data-mp="2860r7dum9mki">
              {logoText}
            </span>
          </div>
          <div className="flex gap-4" data-id="element-2850" data-mp="1y6i8f0qg06u1">
            <Field label="Tier" value={tier.name} tier={tier} align="right" data-id="element-2851" data-mp="1y7jwc5sk4nt6" />
            <Field label="Points" value={walletSample.points} tier={tier} align="right" data-id="element-2852" data-mp="1u6hv5jf2y1ok" />
          </div>
        </div>
        <div className="h-[96px]" style={{
        background: tier.banner
      }} data-id="element-2853" data-mp="3sjrj2vuq5ydv" />
        <div className="px-4 py-3 grid grid-cols-3 gap-2" data-id="element-2854" data-mp="3okitm36pwmo4">
          <Field label="Full name" value={walletSample.fullName} tier={tier} data-id="element-2855" data-mp="1lwsmrqpuqsk7" />
          <Field label="Next tier" value={nextTierName(tier.id)} tier={tier} data-id="element-2856" data-mp="1lupmmduzjj1r" />
          <Field label="Rewards" value={walletSample.rewards} tier={tier} align="right" data-id="element-2857" data-mp="1qp3jylt7k092" />
        </div>
        <div className="flex justify-center pb-4" data-id="element-2858" data-mp="1cxysw83wogac">
          <QrMock data-id="element-2859" data-mp="1gx7ntgaomk7h" />
        </div>
      </div>
      {!compact && <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-2860" data-mp="1gvms3ncmsv81">
          <div className="bg-white rounded-xl p-2.5 border border-[#E5E7EB]" data-id="element-2861" data-mp="24n1uth8blejt">
            <div className="w-6 h-6 rounded-full bg-[#C026D3] flex items-center justify-center mb-1.5" data-id="element-2862" data-mp="29m4v6odcuwlg">
              <Star className="w-3.5 h-3.5 text-white" data-id="element-2863" data-mp="1vn54zca729n6" />
            </div>
            <div className="text-[11px] font-semibold text-[#0F172A] leading-tight" data-id="element-2864" data-mp="1voyer0g3t9ze">View Offers and Rewards</div>
            <div className="text-[10px] text-[#94A3B8]" data-id="element-2865" data-mp="k7q808hzjhs2">Open Link</div>
          </div>
          <div className="bg-white rounded-xl p-2.5 border border-[#E5E7EB]" data-id="element-2866" data-mp="p6jmo490dett">
            <div className="w-6 h-6 rounded-full bg-[#F97316] flex items-center justify-center mb-1.5" data-id="element-2867" data-mp="p4gmire565bd">
              <ShoppingBag className="w-3.5 h-3.5 text-white" data-id="element-2868" data-mp="grlysb6378oh" />
            </div>
            <div className="text-[11px] font-semibold text-[#0F172A] leading-tight" data-id="element-2869" data-mp="cneb8hbd7yru">Shop Online or In-App</div>
            <div className="text-[10px] text-[#94A3B8]" data-id="element-2870" data-mp="qlmch1ffpm8t">Open Link</div>
          </div>
        </div>}
    </div>;
}

/** Lock screen style preview of a push */
export function LockScreenPreview({
  title,
  message,
  logoText = 'Acme Estate',
  platform
}: {
  title: string;
  message: string;
  logoText?: string;
  platform: WalletPlatform;
}) {
  return <div className="rounded-[28px] p-4 w-[300px]" style={{
    background: 'linear-gradient(160deg, #1E3A8A 0%, #6D28D9 55%, #DB2777 100%)'
  }} data-id="element-2871" data-mp="7p4c5vmise0y">
      <div className="text-center text-white/90 mb-1 text-[12px]" data-id="element-2872" data-mp="2wt9il0eae4p">Saturday 8 November</div>
      <div className="text-center text-white text-[52px] font-light leading-none mb-6" data-id="element-2873" data-mp="3bc3afpsjtdaw">9:41</div>
      <div className="bg-white/80 backdrop-blur rounded-2xl p-3 shadow" data-id="element-2874" data-mp="36ebgkq8cfh42">
        <div className="flex items-center gap-2 mb-1" data-id="element-2875" data-mp="2kwl6ngdd26rx">
          <div className="w-5 h-5 rounded-md bg-[#0F172A] flex items-center justify-center" data-id="element-2876" data-mp="2pumnrvr87w0r">
            <Wine className="w-3 h-3 text-white" data-id="element-2877" data-mp="2pwfv7d9nyk9t" />
          </div>
          <span className="text-[11px] font-medium text-[#334155] uppercase tracking-wide flex-1" data-id="element-2878" data-mp="2us4nqwn9n0wm">
            {platform === 'apple' ? 'Wallet' : 'Google Wallet'}
          </span>
          <span className="text-[11px] text-[#64748B]" data-id="element-2879" data-mp="33tcm1eps81qp">now</span>
        </div>
        <div className="text-[13px] font-semibold text-[#0F172A]" data-id="element-2880" data-mp="38syxbim1v7wv">{title || logoText}</div>
        <div className="text-[12px] text-[#334155] whitespace-pre-line" data-id="element-2881" data-mp="3dkh10efgjmlj">{message || 'Your message will appear here.'}</div>
      </div>
    </div>;
}